import { Texture, BaseTexture, Rectangle, SCALE_MODES } from 'pixi.js';
import { useCallback, useEffect, useState } from 'react';
import { TilesetMapping, normalizeLayers } from '../data/tileset.config';
import { TileType } from '../types/map.types';

/**
 * Standard 12×4 blob tileset format.
 * 47 bitmask patterns + 1 empty cell (row 1, col 10) per frame.
 * Animation frames stacked vertically (4 rows per frame).
 */
const BLOB_COLS = 12;
const BLOB_ROWS_PER_FRAME = 4;
const BLOB_SKIP = 22; // 0-based index to skip (row 1, col 10)

interface TilesetMeta {
  tileSize: number;
  animationFrames: number;
  animationSpeed: number;
  /** Visual width of a cell in logical tiles (default 1). */
  artWidth?: number;
  /** Visual height of a cell in logical tiles (default 1). */
  artHeight?: number;
  /** Sprite anchor. Default [0.5, 1] (bottom-center, overflowing upward). */
  artAnchor?: [number, number];
}

const loadBlobTileset = async (
  tilesetName: string,
): Promise<{ textures: Texture[][]; meta: TilesetMeta }> => {
  const res = await fetch(`/tiles/${tilesetName}.json`);
  const meta: TilesetMeta = await res.json();

  const baseTexture = BaseTexture.from(`/tiles/${tilesetName}.png`);
  baseTexture.scaleMode = SCALE_MODES.NEAREST;

  await new Promise<void>((resolve, reject) => {
    if (baseTexture.valid) {
      return resolve();
    }
    baseTexture.on('loaded', () => resolve());
    baseTexture.on('error', reject);
  });

  const ts = meta.tileSize;
  const cellW = ts * (meta.artWidth ?? 1);
  const cellH = ts * (meta.artHeight ?? 1);

  const textures: Texture[][] = [[]]; // index 0 = placeholder

  for (let cellIdx = 0; cellIdx < 48; cellIdx++) {
    if (cellIdx === BLOB_SKIP) {
      continue;
    }

    const col = cellIdx % BLOB_COLS;
    const baseRow = Math.floor(cellIdx / BLOB_COLS);
    const frames: Texture[] = [];

    for (let f = 0; f < meta.animationFrames; f++) {
      const row = f * BLOB_ROWS_PER_FRAME + baseRow;
      const frame = new Rectangle(col * cellW, row * cellH, cellW, cellH);
      frames.push(new Texture(baseTexture, frame));
    }

    textures.push(frames);
  }

  return { textures, meta };
};

/**
 * Simple PNG texture (not a blob tileset): frames laid out horizontally, and
 * all 47 patterns map to the same frame set.
 */
const loadSimpleTexture = async (
  fileName: string,
): Promise<{ textures: Texture[][]; meta: TilesetMeta }> => {
  const res = await fetch(`/tiles/${fileName}.json`);
  const meta: TilesetMeta = await res.json();

  const baseTexture = BaseTexture.from(`/tiles/${fileName}.png`);
  baseTexture.scaleMode = SCALE_MODES.NEAREST;

  await new Promise<void>((resolve, reject) => {
    if (baseTexture.valid) {
      return resolve();
    }
    baseTexture.on('loaded', () => resolve());
    baseTexture.on('error', reject);
  });

  const ts = meta.tileSize;
  const cellW = ts * (meta.artWidth ?? 1);
  const cellH = ts * (meta.artHeight ?? 1);
  const frames: Texture[] = [];
  for (let f = 0; f < meta.animationFrames; f++) {
    frames.push(
      new Texture(baseTexture, new Rectangle(f * cellW, 0, cellW, cellH)),
    );
  }

  const textures: Texture[][] = [[]];
  for (let i = 0; i < 47; i++) {
    textures.push(frames);
  }

  return { textures, meta };
};

export const useTileSprites = (tilesetName: string) => {
  const [textures, setTextures] = useState<Texture[][]>([]);
  const [meta, setMeta] = useState<TilesetMeta>({
    tileSize: 48,
    animationFrames: 1,
    animationSpeed: 0,
  });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    loadBlobTileset(tilesetName).then(({ textures: t, meta: m }) => {
      if (!cancelled) {
        setTextures(t);
        setMeta(m);
        setIsLoaded(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [tilesetName]);

  const getTexture = (patternId: number, animFrame = 0): Texture | null => {
    const frames = textures[patternId];
    if (!frames) {
      return null;
    }
    return frames[animFrame % frames.length] || null;
  };

  const getAnimFrames = (patternId: number): Texture[] | null => {
    return textures[patternId] || null;
  };

  return { isLoaded, meta, getTexture, getAnimFrames };
};

/** Deduplicates: a file referenced by multiple types/layers is loaded once. */
export const useFloorTilesets = (mapping: TilesetMapping) => {
  const [tilesets, setTilesets] = useState<
    Record<string, { textures: Texture[][]; meta: TilesetMeta }>
  >({});
  const [isLoaded, setIsLoaded] = useState(false);

  // Stable string key for the useEffect deps. `Object.values(mapping)` yields
  // a new array ref every render; the `join`-ed version lets React compare by
  // value, otherwise the effect re-runs every frame → tileset reload,
  // setTilesets() → new Textures → GPU re-upload. This was the source of the
  // `texImage2D` saturating the frame.
  const mappingKey = Object.values(mapping).flatMap(normalizeLayers).join('|');

  useEffect(() => {
    let cancelled = false;

    const allNames = Object.values(mapping).flatMap(normalizeLayers);
    const uniqueNames = Array.from(new Set(allNames));
    const tilesetNames = uniqueNames.filter(
      (n) => !n.startsWith('#') && !n.startsWith('~'),
    );
    const colorNames = uniqueNames.filter((n) => n.startsWith('#'));
    const textureNames = uniqueNames.filter((n) => n.startsWith('~'));

    const loadPromises = tilesetNames.map((name) =>
      loadBlobTileset(name)
        .then((data) => [name, data] as const)
        .catch(() => {
          return null;
        }),
    );

    const loadTexturePromises = textureNames.map((prefixed) => {
      const fileName = prefixed.slice(1);
      return loadSimpleTexture(fileName)
        .then((data) => [prefixed, data] as const)
        .catch(() => {
          return null;
        });
    });

    Promise.all([...loadPromises, ...loadTexturePromises]).then((results) => {
      if (cancelled) {
        return;
      }
      const map: Record<string, { textures: Texture[][]; meta: TilesetMeta }> =
        {};
      for (const result of results) {
        if (result) {
          map[result[0]] = result[1];
        }
      }
      for (const hex of colorNames) {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          continue;
        }
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fillRect(0, 0, 1, 1);
        const tex = Texture.from(canvas);
        tex.baseTexture.scaleMode = SCALE_MODES.NEAREST;
        const textures: Texture[][] = [[]];
        for (let i = 0; i < 47; i++) {
          textures.push([tex]);
        }
        map[hex] = {
          textures,
          meta: { tileSize: 1, animationFrames: 1, animationSpeed: 0 },
        };
      }
      setTilesets(map);
      setIsLoaded(true);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappingKey]);

  // All accessors are memoized via `useCallback`: their reference stays stable
  // as long as `mapping` / `tilesets` don't change. Without it they busted the
  // `useMemo` of `renderTiles` in Environment (which has them as deps), causing
  // every sprite to be regenerated on each render.

  const getLayers = useCallback(
    (tileType: TileType): string[] => normalizeLayers(mapping[tileType]),
    [mapping],
  );

  const getTexture = useCallback(
    (patternId: number, tilesetName: string, animFrame = 0): Texture | null => {
      const tileset = tilesets[tilesetName];
      if (!tileset) {
        return null;
      }
      const frames = tileset.textures[patternId];
      if (!frames) {
        return null;
      }
      return frames[animFrame % frames.length] || null;
    },
    [tilesets],
  );

  const getAnimFrames = useCallback(
    (patternId: number, tilesetName: string): Texture[] | null => {
      const tileset = tilesets[tilesetName];
      if (!tileset) {
        return null;
      }
      return tileset.textures[patternId] || null;
    },
    [tilesets],
  );

  const getAnimSpeed = useCallback(
    (tilesetName: string): number =>
      tilesets[tilesetName]?.meta.animationSpeed ?? 0,
    [tilesets],
  );

  const getArtConfig = useCallback(
    (
      tilesetName: string,
    ): { width: number; height: number; anchor: [number, number] } => {
      const m = tilesets[tilesetName]?.meta;
      return {
        width: m?.artWidth ?? 1,
        height: m?.artHeight ?? 1,
        anchor: m?.artAnchor ?? [0.5, 1],
      };
    },
    [tilesets],
  );

  return {
    isLoaded,
    getTexture,
    getAnimFrames,
    getAnimSpeed,
    getLayers,
    getArtConfig,
  };
};
